import apiClient from '../lib/axios';

export type TerminalStatus = 'COMPLETED' | 'CANCELLED';

export interface ReportOrderRow {
  id: string;
  trackingUUID: string;
  status: TerminalStatus;
  patientName: string;
  patientPhone: string | null;
  fulfillmentType: 'PICKUP' | 'DELIVERY';
  totalAmount: string;
  createdAt: string;
  /** Null when a cancellation was never written to the status log. */
  terminalAt: string | null;
}

export interface ReportSummary {
  completedCount: number;
  cancelledCount: number;
  revenue: string;
  avgCompletionMinutes: number | null;
  pickupCount: number;
  deliveryCount: number;
}

export interface ReportRangeQuery {
  from: string;
  to: string;
  status?: TerminalStatus;
  search?: string;
}

export interface ReportListQuery extends ReportRangeQuery {
  page?: number;
  limit?: number;
}

export interface ReportOrdersResponse {
  data: ReportOrderRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

function rangeParams(query: ReportRangeQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.append('from', query.from);
  params.append('to', query.to);
  if (query.status) params.append('status', query.status);
  if (query.search) params.append('search', query.search);
  return params;
}

export async function getReportSummary(query: ReportRangeQuery): Promise<ReportSummary> {
  const { data } = await apiClient.get<ReportSummary>(
    `/pharmacy-chain/reports/summary?${rangeParams(query).toString()}`,
  );
  return data;
}

export async function listReportOrders(query: ReportListQuery): Promise<ReportOrdersResponse> {
  const params = rangeParams(query);
  if (query.page) params.append('page', String(query.page));
  if (query.limit) params.append('limit', String(query.limit));
  const { data } = await apiClient.get<ReportOrdersResponse>(
    `/pharmacy-chain/reports/orders?${params.toString()}`,
  );
  return data;
}

/** Every row in the range, unpaginated. Backs the print view. */
export async function fetchReportRows(query: ReportRangeQuery): Promise<ReportOrderRow[]> {
  const params = rangeParams(query);
  params.append('format', 'json');
  const { data } = await apiClient.get<{ data: ReportOrderRow[] }>(
    `/pharmacy-chain/reports/orders/export?${params.toString()}`,
  );
  return data.data;
}

export function exportReportUrl(query: ReportRangeQuery): string {
  return `/pharmacy-chain/reports/orders/export?${rangeParams(query).toString()}`;
}
